/**
 * plinth.config.json, field by field. The file stands on an easel, tilted
 * back like a chalkboard. The section pins, and each stretch of scroll drops
 * the next field onto the board, in the place it really goes, with a one-line
 * comment above it that says what it is. Only the current field carries a
 * comment, so the board never says more than one thing at a time.
 *
 * The file stays valid JSON at every step (comments are drawn by the tour,
 * never part of the file), and the finished file is a real config the engine
 * reads (see test/config-tour.test.ts). Without JavaScript the page shows the
 * finished file, and the notes as a list.
 */

type Json = string | number | boolean | Json[] | { [key: string]: Json };

/** A field the tour adds, at its path in the file, and what it is in a few words. */
interface Field {
  path: string[];
  value: Json;
  note: string;
}

interface Step {
  /** Who fills it in. */
  source: string;
  fields: Field[];
}

/** In reading order: the essentials first, the rarely needed last. */
export const TOUR_STEPS: Step[] = [
  {
    source: "You, or synced",
    fields: [
      {
        path: ["tables", "orders", "rows"],
        value: 5_000_000,
        note: "How big the table is. Rough is fine.",
      },
    ],
  },
  {
    source: "You, or synced",
    fields: [
      {
        path: ["tables", "orders", "distinct"],
        value: { status: 4, user_id: 380_000 },
        note: "Values per column. Few values, weak index.",
      },
    ],
  },
  {
    source: "You",
    fields: [
      {
        path: ["tables", "orders", "writeHeavy"],
        value: true,
        note: "Your guess: writes dominate this table.",
      },
    ],
  },
  {
    source: "Synced from the primary",
    fields: [
      {
        path: ["stats"],
        value: { hours: 336, statementHours: 336 },
        note: "The two weeks the counters cover.",
      },
      {
        path: ["tables", "orders", "traffic"],
        value: {
          reads: 120_000,
          inserts: 8_400_000,
          updates: 52_000,
          hotUpdates: 41_000,
          deletes: 0,
        },
        note: "Measured: ~25k rows written an hour.",
      },
    ],
  },
  {
    source: "Synced",
    fields: [
      {
        path: ["tables", "orders", "statements"],
        value: [{ op: "select", columns: ["status", "created_at"], calls: 9_400_000 }],
        note: "How often each query ran. No values kept.",
      },
    ],
  },
  {
    source: "Synced",
    fields: [
      {
        path: ["tables", "orders", "indexes"],
        value: [{ name: "orders_region_idx", columns: ["region"], scans: 0 }],
        note: "Real indexes. Never read: unused.",
      },
    ],
  },
  {
    source: "plinth-ci verify",
    fields: [
      {
        path: ["tables", "orders", "planner"],
        value: [
          { columns: ["status", "created_at"], used: true, costBefore: 6588, costAfter: 3.2 },
        ],
        note: "PostgreSQL's verdict on the suggested index.",
      },
    ],
  },
  {
    source: "You, in CI",
    fields: [
      {
        path: ["ignore"],
        value: [
          {
            rule: "unbounded-query",
            file: "src/admin/**",
            reason: "Admin export, runs nightly",
            by: "sam",
          },
        ],
        note: "A team decision, always with a reason.",
      },
    ],
  },
  {
    source: "You",
    fields: [
      { path: ["smallTableRows"], value: 10_000, note: "Smaller tables: Plinth stays quiet." },
      { path: ["largeTableRows"], value: 100_000, note: "Larger tables: high confidence." },
    ],
  },
  {
    source: "Rarely needed",
    fields: [{ path: ["dialect"], value: "postgresql", note: "Read from your ORM if left out." }],
  },
];

/** Where each top-level and table key sits in the file, whatever order steps add them. */
const ORDER = [
  "dialect",
  "smallTableRows",
  "largeTableRows",
  "stats",
  "tables",
  "ignore",
  "rows",
  "distinct",
  "writeHeavy",
  "traffic",
  "statements",
  "indexes",
  "planner",
];

/** The config as it stands after `step` (-1: nothing yet). */
export function configAt(step: number): { [key: string]: Json } {
  const root: { [key: string]: Json } = {};
  for (const { fields } of TOUR_STEPS.slice(0, step + 1)) {
    for (const { path, value } of fields) {
      let node = root;
      for (const key of path.slice(0, -1)) node = (node[key] ??= {}) as { [key: string]: Json };
      node[path.at(-1)!] = value;
    }
  }
  return root;
}

export interface TourLine {
  html: string;
  /** Path of the field this line belongs to, e.g. "tables.orders.traffic". */
  key: string;
}

/**
 * Pretty-print like a hand-written config: objects nested under `tables`
 * expand, leaf objects and arrays of objects stay on one line per entry.
 */
export function renderConfig(config: { [key: string]: Json }): TourLine[] {
  const lines: TourLine[] = [];
  const sorted = (object: { [key: string]: Json }) =>
    Object.entries(object).sort(([a], [b]) => ORDER.indexOf(a) - ORDER.indexOf(b));
  const expand = (key: string, depth: number) => (depth === 0 ? key === "tables" : depth === 1);

  const walk = (object: { [key: string]: Json }, indent: string, path: string[], depth: number) => {
    const entries = sorted(object);
    entries.forEach(([key, value], i) => {
      const comma = i < entries.length - 1 ? "," : "";
      const here = [...path, key].join(".");
      const name = `${indent}<span class="tok-a">"${key}"</span>: `;
      if (isObject(value) && expand(key, depth)) {
        lines.push({ html: `${name}{`, key: here });
        walk(value, `${indent}  `, [...path, key], depth + 1);
        lines.push({ html: `${indent}}${comma}`, key: here });
      } else if (Array.isArray(value) && value.some(isObject)) {
        lines.push({ html: `${name}[`, key: here });
        value.forEach((item, j) =>
          lines.push({
            html: `${indent}  ${inline(item)}${j < value.length - 1 ? "," : ""}`,
            key: here,
          }),
        );
        lines.push({ html: `${indent}]${comma}`, key: here });
      } else {
        lines.push({ html: `${name}${inline(value)}${comma}`, key: here });
      }
    });
  };

  lines.push({ html: "{", key: "" });
  walk(config, "  ", [], 0);
  lines.push({ html: "}", key: "" });
  return lines;
}

/** A line as HTML. Wrapped lines hang under their own indentation, not the margin. */
export function lineHtml(line: TourLine, classes = ""): string {
  const indent = /^ */.exec(line.html)?.[0].length ?? 0;
  const name = ["tour-line", classes].filter(Boolean).join(" ");
  return `<span class="${name}" style="--indent: ${indent}ch">${line.html}</span>`;
}

/**
 * The board after `step`: the file, with the current step's fields marked
 * and a comment line above each of them. `fresh` fields enter the board.
 */
export function boardHtml(step: number, fresh: Set<string> = new Set()): string {
  const lines = renderConfig(configAt(step));
  const current = new Map((TOUR_STEPS[step]?.fields ?? []).map((f) => [f.path.join("."), f.note]));
  const commented = new Set<string>();
  const out: string[] = [];
  for (const line of lines) {
    const note = current.get(line.key);
    const entering = fresh.has(line.key) ? "is-new" : "";
    if (note !== undefined && !commented.has(line.key)) {
      commented.add(line.key);
      const indent = /^ */.exec(line.html)?.[0] ?? "";
      out.push(
        lineHtml(
          { html: `${indent}<span class="tok-c">// ${escape(note)}</span>`, key: line.key },
          ["tour-comment", entering].filter(Boolean).join(" "),
        ),
      );
    }
    out.push(
      lineHtml(line, [note !== undefined ? "is-current" : "", entering].filter(Boolean).join(" ")),
    );
  }
  return out.join("");
}

function isObject(value: Json): value is { [key: string]: Json } {
  return typeof value === "object" && !Array.isArray(value);
}

function inline(value: Json): string {
  if (typeof value === "number") return `<span class="tok-n">${value}</span>`;
  if (typeof value === "boolean") return `<span class="tok-k">${value}</span>`;
  if (typeof value === "string")
    return `<span class="tok-s">${escape(JSON.stringify(value))}</span>`;
  if (Array.isArray(value)) return `[${value.map(inline).join(", ")}]`;
  return `{ ${Object.entries(value)
    .map(([k, v]) => `<span class="tok-a">"${k}"</span>: ${inline(v)}`)
    .join(", ")} }`;
}

function escape(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ── The page ──────────────────────────────────────────────────────────────

export function setUpTour(): void {
  const section = document.querySelector<HTMLElement>("[data-tour]");
  const track = section?.querySelector<HTMLElement>("[data-tour-track]");
  const code = section?.querySelector<HTMLElement>("[data-tour-code]");
  const scroller = section?.querySelector<HTMLElement>("[data-tour-scroll]");
  const source = section?.querySelector<HTMLElement>("[data-tour-source]");
  const count = section?.querySelector<HTMLElement>("[data-tour-count]");
  if (!section || !track || !code || !scroller) return;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const total = TOUR_STEPS.length;

  let shown = Number.NaN;
  let keys = new Set<string>();

  function show(step: number): void {
    if (step === shown) return;
    const forward = Number.isNaN(shown) || step > shown;
    shown = step;
    const lines = renderConfig(configAt(step));
    // Fields that were not on the board a moment ago enter it; scrolling back just removes.
    const fresh = new Set(
      forward && !reduced ? lines.map((l) => l.key).filter((k) => k && !keys.has(k)) : [],
    );
    keys = new Set(lines.map((l) => l.key));
    code!.innerHTML = boardHtml(step, fresh);
    if (source) source.textContent = step >= 0 ? (TOUR_STEPS[step]?.source ?? "") : "";
    if (count) count.textContent = step >= 0 ? `${step + 1} / ${total}` : "";
    const target = code!.querySelector<HTMLElement>(".tour-comment, .is-current");
    if (target) {
      const top = target.offsetTop - scroller!.clientHeight / 3;
      scroller!.scrollTo({ top: Math.max(0, top), behavior: reduced ? "auto" : "smooth" });
    }
  }

  // The first stretch shows the empty file; then one stretch per step.
  function stepAt(): number {
    const rect = track!.getBoundingClientRect();
    const travel = rect.height - innerHeight;
    const p = Math.min(1, Math.max(0, -rect.top / (travel > 0 ? travel : 1)));
    return Math.min(total - 1, Math.floor(p * (total + 1)) - 1);
  }

  let queued = false;
  const update = () => {
    queued = false;
    show(stepAt());
  };
  section.classList.add("is-touring");
  update();
  addEventListener(
    "scroll",
    () => {
      if (!queued) requestAnimationFrame(update);
      queued = true;
    },
    { passive: true },
  );
  addEventListener("resize", update);
}
